import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Users, Shield, ChevronRight, MapPin, FileText, Scale, ClipboardList } from 'lucide-react';
import { API } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';

const AboutPage = () => {
  const { t, language } = useLanguage();
  const [seasonFacts, setSeasonFacts] = useState({});
  const [aboutContent, setAboutContent] = useState({});
  const [venues, setVenues] = useState([]);
  const [venuesTitle, setVenuesTitle] = useState('');
  const [docs, setDocs] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings/season2`).then(res => setSeasonFacts(res.data)).catch(console.error);
    axios.get(`${API}/about-content`).then(res => setAboutContent(res.data.sections || {})).catch(console.error);
    axios.get(`${API}/venues`).then(res => setVenues(res.data)).catch(console.error);
    axios.get(`${API}/settings/venues-title`).then(res => setVenuesTitle(res.data.title)).catch(console.error);
    axios.get(`${API}/documents`).then(res => setDocs(res.data)).catch(console.error);
  }, []);

  // Helper: get editable text or fallback to default
  const txt = (key, defaultGr, defaultEn) => {
    if (aboutContent[key]) return aboutContent[key];
    return language === 'gr' ? defaultGr : defaultEn;
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="about-page">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <h1 className="section-title mb-4">{t('aboutTitle')}</h1>
        <p className="text-slate-600 max-w-2xl mx-auto">{t('aboutSubtitle')}</p>
      </div>

      {/* Mission Section */}
      <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
        <div>
          <img src="https://customer-assets.emergentagent.com/job_deac57eb-5af3-4086-be87-3d979f3fa610/artifacts/wpheix3q_D5246629-C7A0-4803-86B6-1B81D2DFADD5.png" alt="WBLA Logo" className="rounded-2xl shadow-lg w-full max-w-md mx-auto" />
        </div>
        <div>
          <h2 className="text-2xl font-display font-bold text-[#1E3A5F] mb-4">{t('ourMission')}</h2>
          <p className="text-slate-600 mb-4 text-justify whitespace-pre-line">{txt('mission1', t('missionText1'), t('missionText1'))}</p>
          <p className="text-slate-600 mb-4 text-justify whitespace-pre-line">{txt('mission2', t('missionText2'), t('missionText2'))}</p>
          <p className="text-slate-600 text-justify whitespace-pre-line">{txt('mission3', t('missionText3'), t('missionText3'))}</p>
        </div>
      </div>

      {/* Values Section */}
      <div className="grid md:grid-cols-3 gap-8 mb-16">
        {[
          { key: 'value1', title: txt('value1_title', t('excellence'), t('excellence')), desc: txt('value1_desc', t('excellenceDesc'), t('excellenceDesc')) },
          { key: 'value2', title: txt('value2_title', t('community'), t('community')), desc: txt('value2_desc', t('communityDesc'), t('communityDesc')) },
          { key: 'value3', title: txt('value3_title', t('growth'), t('growth')), desc: txt('value3_desc', t('growthDesc'), t('growthDesc')) }
        ].map((value, i) => (
          <div key={i} className="card p-6 text-center" data-testid={`value-${i}`}>
            <div className="w-14 h-14 rounded-full gradient-accent flex items-center justify-center mx-auto mb-4">
              <Shield className="text-white" size={24} />
            </div>
            <h3 className="font-display font-bold text-xl text-[#1E3A5F] mb-2">{value.title}</h3>
            <p className="text-slate-600">{value.desc}</p>
          </div>
        ))}
      </div>

      {/* League Structure Section */}
      <div className="mb-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-display font-bold text-[#1E3A5F] mb-3">
            {language === 'gr' ? 'Δομή 2ης Σεζόν Πρωταθλήματος' : 'Season 2 League Structure'}
          </h2>
          <p className="text-slate-600 max-w-3xl mx-auto whitespace-pre-line">
            {language === 'gr' 
              ? 'Το WBL – Women\'s Basketball League για τη 2η σεζόν διεξάγεται σε δύο βασικές φάσεις'
              : 'The WBL – Women\'s Basketball League is conducted in two main phases for its second season'}
          </p>
        </div>

        {/* Two Phases Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {/* Phase 1: Regular Season */}
          <div className="card p-6 border-l-4 border-[#1E3A5F]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold">1</div>
              <h3 className="font-display font-bold text-xl text-[#1E3A5F]">
                {language === 'gr' ? 'Κανονική Περίοδος' : 'Regular Season'}
              </h3>
            </div>
            <p className="text-slate-600 whitespace-pre-line">
              {language === 'gr'
                ? 'Οι συμμετέχουσες ομάδες αγωνίζονται με σύστημα όλες εναντίον όλων (Double Round Robin). Η τελική κατάταξη διαμορφώνεται βάσει των αποτελεσμάτων της κανονικής περιόδου. Η κανονική περίοδος είναι διπλή για κάθε ομάδα.'
                : 'All participating teams compete in a Double Round Robin format. The final standings are determined based on the regular season results.'}
            </p>
          </div>

          {/* Phase 2: Semifinals & Finals */}
          <div className="card p-6 border-l-4 border-[#F97316]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#F97316] text-white flex items-center justify-center font-bold">2</div>
              <h3 className="font-display font-bold text-xl text-[#1E3A5F]">
                {language === 'gr' ? 'Ημιτελικοί & Τελικοί' : 'Semifinals & Finals'}
              </h3>
            </div>
            <p className="text-slate-600 whitespace-pre-line">
              {language === 'gr'
                ? 'Στους ημιτελικούς η ομάδα της 1ης θέσης αντιμετωπίζει την ομάδα της 4ης και η ομάδα της 2ης θέσης την ομάδα της 3ης. Ακολουθεί ο Μικρός Τελικός (3η θέση) και ο Μεγάλος Τελικός για τον τίτλο και την άνοδο στην Μεγάλη Κατηγορία της 3ης Σεζόν 2027.'
                : 'In the semifinals, 1st place plays 4th and 2nd plays 3rd. Followed by the Small Final (3rd place) and the Grand Final for the title and promotion to the Major League of Season 3 2027.'}
            </p>
          </div>
        </div>

        {/* Tiebreaker Rules */}
        <div className="card p-6 mb-10">
          <h3 className="font-display font-bold text-xl text-[#1E3A5F] mb-4 flex items-center gap-2">
            <Scale size={24} className="text-[#F97316]" />
            {language === 'gr' ? 'Κριτήρια Ισοβαθμίας' : 'Tiebreaker Rules'}
          </h3>
          <p className="text-slate-600 mb-4">
            {language === 'gr' 
              ? 'Σε περίπτωση ισοβαθμίας δύο ή περισσότερων ομάδων, η κατάταξη καθορίζεται σύμφωνα με τους κανονισμούς της FIBA:'
              : 'In case of a tie between two or more teams, the ranking is determined according to FIBA regulations:'}
          </p>
          <ol className="space-y-2 text-slate-600">
            {(language === 'gr' 
              ? ['Αποτελέσματα μεταξύ των ισοβαθμούντων ομάδων', 'Διαφορά πόντων στα μεταξύ τους παιχνίδια', 'Συνολική διαφορά πόντων στην κανονική περίοδο', 'Συνολικοί πόντοι υπέρ', 'Κλήρωση (εφόσον απαιτηθεί)']
              : ['Head-to-head results between tied teams', 'Point difference in head-to-head games', 'Overall point difference in regular season', 'Total points scored', 'Draw (if necessary)']
            ).map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</span>
                {item}
              </li>
            ))}
          </ol>
        </div>

        {/* Tournament Path Format */}
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A87] rounded-2xl p-8 text-white mb-10">
          <h3 className="font-display font-bold text-2xl mb-6 text-center">
            {language === 'gr' ? 'Πορεία προς την άνοδο στην Μεγάλη Κατηγορία' : 'Road to Promotion to the Major League'}
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-bold text-lg mb-3 text-[#F97316]">
                {language === 'gr' ? 'Ημιτελικοί' : 'Semifinals'}
              </h4>
              <div className="space-y-2">
                <div className="bg-white/10 rounded-lg p-3 flex items-center justify-between text-sm">
                  <span>1η</span>
                  <span className="text-[#F97316] font-bold">VS</span>
                  <span>4η</span>
                </div>
                <div className="bg-white/10 rounded-lg p-3 flex items-center justify-between text-sm">
                  <span>2η</span>
                  <span className="text-[#F97316] font-bold">VS</span>
                  <span>3η</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-3 text-[#F97316]">
                {language === 'gr' ? 'Μικρός Τελικός' : 'Small Final'}
              </h4>
              <div className="space-y-2">
                <div className="bg-white/10 rounded-lg p-3 flex items-center justify-between text-sm">
                  <span>{language === 'gr' ? 'Ηττ. Ημ. 1' : 'Loser SF1'}</span>
                  <span className="text-[#F97316] font-bold">VS</span>
                  <span>{language === 'gr' ? 'Ηττ. Ημ. 2' : 'Loser SF2'}</span>
                </div>
                <div className="bg-white/10 rounded-lg p-2 text-xs text-white/60 text-center">
                  {language === 'gr' ? '3η θέση' : '3rd place'}
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-3 text-[#F97316]">
                {language === 'gr' ? 'Μεγάλος Τελικός' : 'Grand Final'}
              </h4>
              <div className="space-y-2">
                <div className="bg-white/10 rounded-lg p-3 flex items-center justify-between text-sm">
                  <span>{language === 'gr' ? 'Νικ. Ημ. 1' : 'Winner SF1'}</span>
                  <span className="text-[#F97316] font-bold">VS</span>
                  <span>{language === 'gr' ? 'Νικ. Ημ. 2' : 'Winner SF2'}</span>
                </div>
                <div className="bg-white/10 rounded-lg p-2 text-xs text-[#F97316] text-center font-bold">
                  {language === 'gr' ? 'Τίτλος & Άνοδος στη Μεγάλη Κατηγορία 2027' : 'Title & Promotion to Major League 2027'}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Award Ceremony Section */}
        <div className="card p-6 mb-10 border-l-4 border-[#F97316]">
          <h3 className="font-display font-bold text-xl text-[#1E3A5F] mb-4 flex items-center gap-2">
            <Trophy size={24} className="text-[#F97316]" />
            {language === 'gr' ? 'Απονομή & Λήξη Πρωταθλήματος' : 'Award Ceremony & Season End'}
          </h3>
          <p className="text-slate-600 mb-4">
            {language === 'gr' 
              ? 'Μετά την ολοκλήρωση του Μεγάλου Τελικού της 2ης Σεζόν του WBLA, θα ακολουθήσει τελετή απονομής, κατά την οποία:'
              : 'After the Grand Final of the 2nd Season of WBLA, an award ceremony will follow, during which:'}
          </p>
          <ul className="space-y-2 text-slate-600 mb-4">
            <li className="flex items-start gap-2">
              <ChevronRight size={18} className="text-[#F97316] flex-shrink-0 mt-0.5" />
              {language === 'gr' ? 'Θα απονεμηθεί το κύπελλο στην πρωταθλήτρια ομάδα' : 'The trophy will be awarded to the champion team'}
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight size={18} className="text-[#F97316] flex-shrink-0 mt-0.5" />
              {language === 'gr' ? 'H MVP της 2ης Σεζόν θα λάβει το μετάλλιο και το έπαθλο της' : 'The MVP of Season 2 will receive her medal and award'}
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight size={18} className="text-[#F97316] flex-shrink-0 mt-0.5" />
              {language === 'gr' ? 'Οι τρεις φιναλίστ θα λάβουν μετάλλιο' : 'The three finalists will receive medals'}
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight size={18} className="text-[#F97316] flex-shrink-0 mt-0.5" />
              {language === 'gr' ? 'Θα ανακοινωθούν και απονεμηθούν τα έπαθλα της διοργάνωσης' : 'The tournament prizes will be announced and awarded'}
            </li>
          </ul>
          <p className="text-slate-600 italic border-t border-slate-200 pt-4">
            {language === 'gr' 
              ? 'Η τελετή απονομής σηματοδοτεί την επίσημη λήξη της αγωνιστικής σεζόν του WBLA.'
              : 'The award ceremony marks the official end of the WBLA competitive season.'}
          </p>
        </div>

        {/* Game Regulations */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="card p-6">
            <h3 className="font-display font-bold text-xl text-[#1E3A5F] mb-4 flex items-center gap-2">
              <FileText size={24} className="text-[#F97316]" />
              {language === 'gr' ? 'Κανονισμοί Αγώνων' : 'Game Regulations'}
            </h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start gap-2">
                <ChevronRight size={18} className="text-[#F97316] flex-shrink-0 mt-0.5" />
                {language === 'gr' 
                  ? 'Όλοι οι αγώνες διεξάγονται σύμφωνα με τους επίσημους κανονισμούς της FIBA'
                  : 'All games are played according to official FIBA rules'}
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight size={18} className="text-[#F97316] flex-shrink-0 mt-0.5" />
                {language === 'gr' 
                  ? 'Επίσημη μπάλα αγώνων: Μέγεθος 6 (Size 6)'
                  : 'Official game ball: Size 6'}
              </li>
            </ul>
          </div>
          <div className="card p-6">
            <h3 className="font-display font-bold text-xl text-[#1E3A5F] mb-4 flex items-center gap-2">
              <Users size={24} className="text-[#F97316]" />
              {language === 'gr' ? 'Αρχές Διοργάνωσης' : 'Organization Principles'}
            </h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start gap-2">
                <ChevronRight size={18} className="text-[#F97316] flex-shrink-0 mt-0.5" />
                {language === 'gr' ? 'Σεβασμός προς τις αθλήτριες και τους διαιτητές' : 'Respect for athletes and referees'}
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight size={18} className="text-[#F97316] flex-shrink-0 mt-0.5" />
                {language === 'gr' ? 'Αθλητική συμπεριφορά και fair play' : 'Sportsmanship and fair play'}
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight size={18} className="text-[#F97316] flex-shrink-0 mt-0.5" />
                {language === 'gr' ? 'Οργάνωση, συνέπεια και διαφάνεια' : 'Organization, consistency and transparency'}
              </li>
            </ul>
          </div>
        </div>

        {/* Participation Requirements */}
        <div className="card p-6 mb-10">
          <h3 className="font-display font-bold text-xl text-[#1E3A5F] mb-4 flex items-center gap-2">
            <ClipboardList size={24} className="text-[#F97316]" />
            {language === 'gr' ? 'Προϋποθέσεις Συμμετοχής' : 'Participation Requirements'}
          </h3>
          <p className="text-slate-600 mb-4">
            {language === 'gr' 
              ? 'Για να ολοκληρωθεί η συμμετοχή μιας αθλήτριας στο WBL – Women\'s Basketball League, απαιτούνται τα εξής:'
              : 'To complete registration as an athlete in WBL – Women\'s Basketball League, the following are required:'}
          </p>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-semibold text-[#1E3A5F] mb-2">1. {language === 'gr' ? 'Υπογεγραμμένη Δήλωση Συμμετοχής' : 'Signed Participation Declaration'}</h4>
              <p className="text-slate-600 text-sm mb-3">
                {language === 'gr' 
                  ? 'Η αθλήτρια οφείλει να υπογράψει την επίσημη Δήλωση Συμμετοχής στο WBL.'
                  : 'The athlete must sign the official WBL Participation Declaration.'}
              </p>
              <a href={docs.participation ? `${API}/documents/participation/file` : "https://customer-assets.emergentagent.com/job_deac57eb-5af3-4086-be87-3d979f3fa610/artifacts/8nlalxt6_%CE%94%CE%97%CE%9B%CE%A9%CE%A3%CE%97%20%CE%A3%CE%A5%CE%9C%CE%9C%CE%95%CE%A4%CE%9F%CE%A7%CE%97%CE%A3%20%CE%A3%CE%A4%CE%9F%20WBL%20%E2%80%93%20WOMEN%E2%80%99S%20BASKETBALL%20LEAGUE.docx"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg text-sm hover:bg-[#2D5A87] transition" data-testid="download-participation-form">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {language === 'gr' ? 'Κατεβάστε τη Δήλωση Συμμετοχής' : 'Download Participation Form'}
              </a>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-semibold text-[#1E3A5F] mb-2">2. {language === 'gr' ? 'Δήλωση Ανάληψης Κινδύνου & Αποποίησης Ευθύνης' : 'Risk Assumption & Liability Waiver'}</h4>
              <p className="text-slate-600 text-sm mb-3">
                {language === 'gr' 
                  ? 'Απαιτείται υπογεγραμμένη Δήλωση Ανάληψης Κινδύνου, με την οποία η συμμετέχουσα αναγνωρίζει τους εγγενείς κινδύνους του αθλήματος.'
                  : 'A signed Risk Assumption Declaration is required, acknowledging the inherent risks of the sport.'}
              </p>
              <a href={docs.waiver ? `${API}/documents/waiver/file` : "https://customer-assets.emergentagent.com/job_deac57eb-5af3-4086-be87-3d979f3fa610/artifacts/6wccts0m_%CE%94%CE%97%CE%9B%CE%A9%CE%A3%CE%97%20%CE%91%CE%9D%CE%91%CE%9B%CE%97%CE%A8%CE%97%CE%A3%20%CE%9A%CE%99%CE%9D%CE%94%CE%A5%CE%9D%CE%9F%CE%A5%20%26%20%CE%91%CE%A0%CE%9F%CE%A0%CE%9F%CE%99%CE%97%CE%A3%CE%97%CE%A3%20%CE%95%CE%A5%CE%98%CE%A5%CE%9D%CE%97%CE%A3.docx"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg text-sm hover:bg-[#2D5A87] transition" data-testid="download-waiver-form">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {language === 'gr' ? 'Κατεβάστε τη Δήλωση Ανάληψης Κινδύνου' : 'Download Risk Waiver Form'}
              </a>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-semibold text-[#1E3A5F] mb-2">3. {language === 'gr' ? 'Ιατρική Βεβαίωση' : 'Medical Certificate'}</h4>
              <p className="text-slate-600 text-sm">
                {language === 'gr' 
                  ? 'Η αθλήτρια οφείλει να προσκομίσει έγκυρη ιατρική βεβαίωση καταλληλότητας για συμμετοχή σε αγωνιστική δραστηριότητα καλαθοσφαίρισης.'
                  : 'The athlete must provide a valid medical certificate confirming fitness for basketball competition.'}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-semibold text-[#1E3A5F] mb-2">4. {language === 'gr' ? 'Κάρτα Υγείας Αθλητή' : 'Athlete Health Card'}</h4>
              <p className="text-slate-600 text-sm">
                {language === 'gr' 
                  ? 'Εάν η παίκτρια κατέχει ενεργή Κάρτα Υγείας Αθλητή, δεν χρειάζεται να προσκομίσει ιατρική βεβαίωση.'
                  : 'If the player holds a valid Athlete Health Card, no medical certificate is required.'}
              </p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm font-medium">
              {language === 'gr' 
                ? 'Χωρίς τα ανωτέρω έγγραφα δεν επιτρέπεται η συμμετοχή σε αγώνα. Η διοργάνωση διατηρεί το δικαίωμα ελέγχου των δικαιολογητικών πριν από την έναρξη των αγώνων.'
                : 'Without the above documents, participation in games is not permitted. The organization reserves the right to verify documents before games begin.'}
            </p>
          </div>
        </div>

        {/* Future Development Note */}
        <div className="bg-slate-100 rounded-xl p-6 text-center">
          <p className="text-slate-600 italic">
            {language === 'gr' 
              ? 'Η διοργάνωση διατηρεί το δικαίωμα μελλοντικής διαμόρφωσης αγωνιστικών κατηγοριών (Divisions) με σύστημα ανόδου και υποβιβασμού, ανάλογα με τον αριθμό και το επίπεδο των συμμετεχουσών ομάδων.'
              : 'The organization reserves the right to create competitive divisions with promotion and relegation systems in the future, depending on the number and level of participating teams.'}
          </p>
        </div>
      </div>

      {/* Venues Section - Dynamic */}
      {venues.length > 0 && (
        <div className="space-y-6 mb-12">
          <h2 className="text-3xl font-display font-bold text-[#1E3A5F] text-center mb-8" data-testid="about-venues-title">
            {venuesTitle}
          </h2>
          {venues.map((venue, idx) => (
            <div key={venue.id || idx} className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A87] rounded-2xl p-8 text-white" data-testid={`about-venue-${idx}`}>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h4 className="text-xl text-[#F97316] font-bold mb-3">{venue.name}</h4>
                  {venue.description && <p className="text-white/80 mb-4">{venue.description}</p>}
                  {venue.address && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-white text-[#1E3A5F] px-5 py-2.5 rounded-lg font-semibold hover:bg-slate-100 transition"
                      data-testid={`about-venue-map-${idx}`}
                    >
                      <MapPin size={20} />
                      {language === 'gr' ? 'Δείτε στο Google Maps' : 'View on Google Maps'}
                    </a>
                  )}
                </div>
                {venue.map_embed && (
                  <div className="rounded-xl overflow-hidden shadow-lg">
                    <iframe src={venue.map_embed} width="100%" height="250" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={venue.name}></iframe>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Season Stats */}
      <div className="bg-slate-100 rounded-2xl p-8 md:p-12 mb-12" data-testid="season-facts-section">
        <h2 className="text-2xl font-display font-bold text-[#1E3A5F] mb-6 text-center">{t('seasonFacts')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { num: seasonFacts.teams_count, label: t('teamsCount') },
            { num: seasonFacts.players_count, label: t('playersCount') },
            { num: seasonFacts.games_count, label: t('gamesCount') },
            { num: seasonFacts.champion, label: t('champion') }
          ].map((stat, i) => (
            <div key={i} data-testid={`stat-${i}`}>
              <p className="text-4xl font-display font-bold text-[#F97316]">{stat.num}</p>
              <p className="text-slate-600">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Links to subpages */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Link to="/privacy" className="card p-6 hover-lift flex items-center gap-4" data-testid="privacy-link">
          <div className="w-12 h-12 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
            <FileText className="text-[#1E3A5F]" size={24} />
          </div>
          <div>
            <h3 className="font-bold text-[#1E3A5F]">{t('privacyPolicy')}</h3>
            <p className="text-sm text-slate-500">GDPR</p>
          </div>
          <ChevronRight className="ml-auto text-slate-400" />
        </Link>
        <Link to="/terms" className="card p-6 hover-lift flex items-center gap-4" data-testid="terms-link">
          <div className="w-12 h-12 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
            <Scale className="text-[#1E3A5F]" size={24} />
          </div>
          <div>
            <h3 className="font-bold text-[#1E3A5F]">{t('termsOfUse')}</h3>
            <p className="text-sm text-slate-500">{t('legal')}</p>
          </div>
          <ChevronRight className="ml-auto text-slate-400" />
        </Link>
      </div>

    </div>
  );
};

export default AboutPage;
